import { components } from "@/api-schema";
import { DatVehicleValuationErrorCode } from "@/api-schema-enums";
import { isAxiosError } from "axios";
import i18next from "i18next";

const DAT_DAILY_VIN_REQUEST_LIMIT_ERROR_TRANSLATION_KEY = "fab:VehicleValuationDailyVinRequestLimitReached" as const;
const DAT_INITIAL_REGISTRATION_TO_MARKET_INDEX_ERROR_TRANSLATION_KEY =
  "fab:VehicleValuationInitialRegistrationDateMismatch" as const;
const DAT_INVALID_VIN_ERROR_TRANSLATION_KEY = "fab:VehicleValuationInvalidVin" as const;
const DAT_INVALID_ECODE_ERROR_TRANSLATION_KEY = "fab:VehicleValuationInvalidDatCode" as const;

export const VEHICLE_VALUATION_CUSTOM_ERRORS = {
  400: null,
  422: null,
} as const satisfies Record<number, string | null>;

type VehicleValuationApiErrorResponse = components["schemas"]["DatVehicleValuationErrorResponseDto"];

function isVehicleValuationApiErrorResponse(value: unknown): value is VehicleValuationApiErrorResponse {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<VehicleValuationApiErrorResponse>;
  const isSupportedErrorCode =
    candidate.errorCode === DatVehicleValuationErrorCode.DailyVinRequestLimitReached ||
    candidate.errorCode === DatVehicleValuationErrorCode.InitialRegistrationToMarketIndex ||
    candidate.errorCode === DatVehicleValuationErrorCode.InvalidVin ||
    candidate.errorCode === DatVehicleValuationErrorCode.InvalidDatEcode;

  return isSupportedErrorCode && typeof candidate.message === "string" && typeof candidate.statusCode === "number";
}

function getVehicleValuationApiErrorResponse(error: unknown): VehicleValuationApiErrorResponse | null {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return null;
  }

  const responseData = error.response?.data;
  return isVehicleValuationApiErrorResponse(responseData) ? responseData : null;
}

function getVehicleValuationTranslationKey(errorCode: DatVehicleValuationErrorCode): string | null {
  switch (errorCode) {
    case DatVehicleValuationErrorCode.DailyVinRequestLimitReached:
      return DAT_DAILY_VIN_REQUEST_LIMIT_ERROR_TRANSLATION_KEY;
    case DatVehicleValuationErrorCode.InitialRegistrationToMarketIndex:
      return DAT_INITIAL_REGISTRATION_TO_MARKET_INDEX_ERROR_TRANSLATION_KEY;
    case DatVehicleValuationErrorCode.InvalidVin:
      return DAT_INVALID_VIN_ERROR_TRANSLATION_KEY;
    case DatVehicleValuationErrorCode.InvalidDatEcode:
      return DAT_INVALID_ECODE_ERROR_TRANSLATION_KEY;
    default:
      return null;
  }
}

export function resolveVehicleValuationErrorDescription(error: unknown): string | null {
  const responseData = getVehicleValuationApiErrorResponse(error);
  if (!responseData) {
    return null;
  }

  const translationKey = getVehicleValuationTranslationKey(responseData.errorCode);
  if (translationKey) {
    return i18next.t(translationKey);
  }

  return null;
}
